import { type FC, useEffect, useState } from 'react';
import * as uuid from 'uuid';

import { type Timeline, type TimelineSlide } from '@editor2/types/pageElements';
import { updateElement } from '@editor2/reducers/editor/editorSlice';
import SlideEditView from './SlideEditView';
import SlidesPanel from './SlidesPanel';

import { elementCommonStyles } from '@editor2/styles/element';
import { timelineElement } from './styles';
import ImportDataBlock from './ImportDataBlock';
import FigurePropertiesBlock from './FigurePropertiesBlock';
import { useDebouncedDispatch } from '@editor2/hooks/useDebouncedDispatch';

interface Props {
	element: Timeline;
	courseId: number;
}

const TimelineElement: FC<Props> = (props) => {
	const { element, courseId } = props;

	const commonPayload = { elementId: element.family_id, courseId };

	const debouncedDispatch = useDebouncedDispatch();

	const getInitActiveSlideIndex = () => {
		if (element.data.events.length) return 0;
		if (element.data.title) return -1;
		return null;
	};

	const [slides, setSlides] = useState(element.data.events);
	const [activeSlideIndex, setActiveSlideIndex] = useState(getInitActiveSlideIndex()); // if activeSlideIndex is -1 - it's a title slide

	useEffect(() => {
		if (JSON.stringify(element.data.events) !== JSON.stringify(slides)) {
			setSlides(element.data.events);
		}
	}, [element]);

	const handleChange = (value) =>
		debouncedDispatch(
			updateElement({
				...commonPayload,
				data: { ...element.data, ...value }
			})
		);

	const handleChangeStartSlideIndex = (index: number | null) => {
		const startAtSlide = index == null ? undefined : index;
		debouncedDispatch(
			updateElement({
				...commonPayload,
				data: element.data,
				options: {
					...element.options,
					start_at_slide: startAtSlide
				}
			} satisfies Partial<Timeline>)
		);
	};

	const isTitleSlide = (slideIndex: number) => slideIndex === -1;

	const handleSlideUpdate = (slide: TimelineSlide) => {
		if (isTitleSlide(activeSlideIndex)) {
			handleChange({ title: slide });
		} else {
			const updatedSlides = [
				...slides.slice(0, activeSlideIndex),
				slide,
				...slides.slice(activeSlideIndex + 1)
			];

			setSlides(updatedSlides);
			handleChange({ events: updatedSlides });
		}
	};

	const handleAddSlideClick = () => {
		const newSlide: TimelineSlide = getNewSlide();
		setSlides([...slides, newSlide]);

		handleChange({ events: [...slides, newSlide] });
	};

	const handleAddTitleSlideClick = () => {
		if (element.options?.start_at_slide != null) {
			debouncedDispatch(
				updateElement({
					...commonPayload,
					data: {
						...element.data,
						title: { unique_id: uuid.v4() }
					},
					options: { start_at_slide: element.options.start_at_slide + 1 }
				})
			);
		} else {
			handleChange({ title: { unique_id: uuid.v4() } });
		}
	};

	const getActiveSlideHeadline = () => {
		return isTitleSlide(activeSlideIndex)
			? element.data.title?.text?.headline
			: slides[activeSlideIndex].text?.headline;
	};

	const handleDeleteSlideClick = () => {
		// eslint-disable-next-line no-alert
		if (
			window.confirm(`Are you sure you want to delete "${getActiveSlideHeadline() || ''}" slide?`)
		) {
			if (isTitleSlide(activeSlideIndex)) {
				if (element.options?.start_at_slide != null) {
					debouncedDispatch(
						updateElement({
							...commonPayload,
							data: { ...element.data, title: null },
							options: { start_at_slide: element.options.start_at_slide - 1 }
						})
					);
				} else {
					handleChange({ title: null });
				}
				setActiveSlideIndex(null);
			} else {
				const updatedSlides = [
					...slides.slice(0, activeSlideIndex),
					...slides.slice(activeSlideIndex + 1)
				];
				const updatedIndex =
					activeSlideIndex === slides.length - 1 ? activeSlideIndex - 1 : activeSlideIndex;
				setActiveSlideIndex(updatedIndex === -1 && !element.data?.title ? null : updatedIndex);
				setSlides(updatedSlides);

				handleChange({ events: updatedSlides });
			}
		}
	};

	const handleSlidesReorder = (reorderedSlides) => {
		setSlides(reorderedSlides);

		handleChange({ events: reorderedSlides });
	};

	const handleDataImport = (data) => handleChange(data);

	return (
		<div className={elementCommonStyles}>
			<FigurePropertiesBlock element={element} courseId={courseId} />
			<ImportDataBlock onDataImport={handleDataImport} />

			<div className={timelineElement}>
				<SlidesPanel
					slides={slides}
					startSlideIndex={element.options?.start_at_slide}
					titleSlide={element.data.title}
					activeSlideIndex={activeSlideIndex}
					setActiveSlideIndex={setActiveSlideIndex}
					onAddSlideClick={handleAddSlideClick}
					onAddTitleSlideClick={handleAddTitleSlideClick}
					onSlidesReorder={handleSlidesReorder}
					onChangeStartSlideIndex={handleChangeStartSlideIndex}
				/>

				<div className="slideBlock">
					{activeSlideIndex !== null && (
						<SlideEditView
							key={activeSlideIndex}
							slide={activeSlideIndex === -1 ? element.data.title : slides[activeSlideIndex]}
							isTitleSlide={activeSlideIndex === -1}
							onDeleteSlideClick={handleDeleteSlideClick}
							onSlideUpdate={handleSlideUpdate}
						/>
					)}

					{activeSlideIndex === null && (
						<div className="noSlideSelectedBlock">Choose a slide to edit</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default TimelineElement;

function getNewSlide(): TimelineSlide {
	return {
		start_date: { year: new Date().getFullYear() },
		unique_id: uuid.v4(),
		media: { url: '' }
	};
}
